import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { LineChartOutlined } from '@ant-design/icons';
import ChartEmptyState from './ChartEmptyState';
import { truncateChartLabel } from '../utils/chartLabels';
import './ModelLatency.css';

const COLORS = {
  p50: '#10b981',
  average: '#3b82f6',
  p95: '#f59e0b',
  p99: '#ef4444',
};

const METRIC_LABELS = {
  p50: 'P50',
  average: 'Average',
  p95: 'P95',
  p99: 'P99',
};

const ModelLatencyChart = ({
  data,
  height = 350,
  stretch = false,
  emptyState = null,
  onBarClick,
}) => {
  const chartData = data && data.length > 0 ? data : [];

  const customTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="chart-tooltip">
        <div className="chart-tooltip__title">{label}</div>
        {payload.map((entry) => (
          <div key={entry.dataKey} className="chart-tooltip__metric-row">
            <span className="chart-tooltip__metric-label">
              <span className="chart-tooltip__swatch" style={{ background: entry.color }} />
              {METRIC_LABELS[entry.dataKey] || entry.dataKey}
            </span>
            <strong>{entry.value}ms</strong>
          </div>
        ))}
      </div>
    );
  };

  const isFixedHeight = typeof height === 'number';
  const outerBase = isFixedHeight
    ? { width: '100%', height, minHeight: height, boxSizing: 'border-box' }
    : (stretch
      ? { width: '100%', flex: 1, minHeight: 240, height: '100%', boxSizing: 'border-box' }
      : { width: '100%', minHeight: 240, height: height || '100%', boxSizing: 'border-box' });

  if (emptyState) {
    return (
      <ChartEmptyState
        icon={LineChartOutlined}
        title={emptyState.title}
        description={emptyState.description}
        actionLabel={emptyState.actionLabel}
        onAction={emptyState.onAction}
        tone={emptyState.tone}
        height={height}
        minHeight={240}
      />
    );
  }

  if (!chartData.length) return null;

  return (
    <div className="model-latency-chart" style={outerBase}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 12, right: 12, left: 4, bottom: 8 }}
          barCategoryGap="20%"
          barGap={2}
          onClick={onBarClick ? (state) => {
            if (state?.activeLabel) onBarClick(state.activeLabel);
          } : undefined}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-light)" vertical={false} />
          <XAxis
            dataKey="model"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
            interval={0}
            tickFormatter={(value) => truncateChartLabel(value, 16)}
            height={48}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
            tickFormatter={(v) => `${v}ms`}
            width={52}
          />
          <Tooltip content={customTooltip} />
          <Legend
            verticalAlign="bottom"
            height={28}
            iconType="square"
            iconSize={10}
            wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
            formatter={(value) => METRIC_LABELS[value] || value}
          />
          <Bar dataKey="p50" fill={COLORS.p50} radius={[4, 4, 0, 0]} name="p50" />
          <Bar dataKey="average" fill={COLORS.average} radius={[4, 4, 0, 0]} name="average" />
          <Bar dataKey="p95" fill={COLORS.p95} radius={[4, 4, 0, 0]} name="p95" />
          <Bar dataKey="p99" fill={COLORS.p99} radius={[4, 4, 0, 0]} name="p99" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ModelLatencyChart;
